import { ApprovalsPersons } from "@/components/approvals/ApprovalsPersons.tsx";
import { FileItem } from "@/components/files/FileItem.tsx";
import {
  ConfirmationModal,
  HtmlViewer,
  Input,
  Switch,
  useModals,
  useToasts,
  Modal,
  ModalProps,
  Avatar,
  Box,
  Button,
  ButtonLink,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  FontAwesomeIcon,
  LinkTo,
  ListItem,
  ListOption,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ChoiceJobForPositionRequestModal } from "@/pages/recruit/hiring-plan/widgets/ChoiceJobForPositionRequestModal.tsx";
import { RequestPositionEditModal } from "@/pages/recruit/hiring-plan/widgets/RequestPositionEditModal.tsx";
import {
  useApprovalRequestCancel,
  useConnectPositionRequestToJob,
  useHiringPlanDelete,
  usePositionRequestAnswer,
  usePositionRequestDetails,
} from "@/queries/recruit/use-hiring-plan.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { getJobProfileTitle } from "@/types/job-profile.types.ts";
import {
  ApiHiringPlanStatus,
  getHiringPlanRequestStatusColor,
  getHiringPlanRequestStatusTitle,
} from "@/types/recruit/hiring-plan.types.ts";
import { JobStatusItems } from "@/types/recruit/job.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useEffectOnce } from "react-use";

interface PositionRequestModalProps extends ModalProps {
  requestId?: number;
}

export function HiringPlanRequestViewModal({ requestId, ...props }: PositionRequestModalProps) {
  const { id } = props;
  const { t } = useTranslation();
  const location = useLocation();
  const { data: request, isLoading } = usePositionRequestDetails(requestId, !!requestId);
  const { renderDate, renderCurrency } = useFormatter();
  const { openModal, close } = useModals();
  const requestAnswer = usePositionRequestAnswer();
  const requestCancel = useApprovalRequestCancel();
  const requestDelete = useHiringPlanDelete();
  const requestConnectToJob = useConnectPositionRequestToJob();
  const { canIEdit } = usePermissions();
  const { showInfo } = useToasts();

  const canManageRequests = canIEdit(ApiPermission.manage_hiring_plan);
  const [isApproveButtonShouldLoading, setIsApprove] = useState<boolean>(false);
  const [useComment, setUseComment] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");

  const captionTitle = useMemo(() => {
    if (!request) return "";

    const titles: string[] = [];

    if (request.hiring_plan_data.location) {
      titles.push(request.hiring_plan_data.location.title);
    }

    if (request.hiring_plan_data.department) {
      titles.push(request.hiring_plan_data.department.title);
    }

    if (request.hiring_plan_data.division) {
      titles.push(request.hiring_plan_data.division.title);
    }

    return titles.join(" · ");
  }, [request]);

  const handleAnswerRequest = async (approve: boolean, isBypassApprovalFlow: boolean = false) => {
    if (!request?.approval_request_details) return;

    setIsApprove(approve);

    const requestId = request.approval_request_details.id;
    const bypass = canManageRequests ? isBypassApprovalFlow : false;

    const payload = {
      id: requestId,
      approve,
      payload: { is_bypass_approval_flow: bypass, comment: useComment ? comment : null },
    };

    const trackAnalytics = () => {
      analyticsService.trackEvent(analyticEvents.hiringPlan.requestAction, {
        [analyticProperties.id]: requestId,
        [analyticProperties.title]: "Position Request",
        [analyticProperties.actionType]: approve ? "Approve" : "Reject",
      });
    };

    if (canManageRequests && isBypassApprovalFlow) {
      openModal(ConfirmationModal, {
        question: t(approve ? "approval:approve_as_admin" : "approval:reject_as_admin"),
        text: t(
          approve ? "approval:approve_as_admin_description" : "approval:reject_as_admin_description"
        ),
        acceptTitle: t(approve ? "approval:approve" : "approval:reject"),
        onAccept: async function () {
          try {
            await requestAnswer.mutateAsync(payload);
            showInfo({
              title: approve ? t("approval:request_approved") : t("approval:request_rejected"),
            });
            trackAnalytics();
          } catch {
            return;
          }
        },
      });
    } else {
      try {
        await requestAnswer.mutateAsync(payload, {
          onSuccess: trackAnalytics,
        });
      } catch {
        return;
      }
    }
  };

  const handleLinkToJob = useCallback(() => {
    if (!request || !request.approval_request_details) return;

    openModal(ChoiceJobForPositionRequestModal, {
      onSelect: async (id) => {
        if (!request || !request.approval_request_details) return;

        try {
          await requestConnectToJob.mutateAsync(
            { requestId: request.approval_request_details.id, jobId: id },
            {
              onSuccess: () => {
                analyticsService.trackEvent(analyticEvents.hiringPlan.requestAction, {
                  [analyticProperties.id]: request.approval_request_details?.id,
                  [analyticProperties.title]: "Position Request",
                  [analyticProperties.actionType]: "Connect Job",
                });
              },
            }
          );
        } catch {
          return;
        }
      },
    });
  }, [request]);

  const handleUnlinkToJob = useCallback(async () => {
    if (!request || !request.approval_request_details) return;
    try {
      await requestConnectToJob.mutateAsync(
        { requestId: request.approval_request_details.id, jobId: null },
        {
          onSuccess: () => {
            analyticsService.trackEvent(analyticEvents.hiringPlan.requestAction, {
              [analyticProperties.id]: request.approval_request_details?.id,
              [analyticProperties.title]: "Position Request",
              [analyticProperties.actionType]: "Disconnect Job",
            });
          },
        }
      );
    } catch {
      return;
    }
  }, [request]);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.hiringPlan.requestViewed, {
      [analyticProperties.id]: request?.approval_request_details?.id,
    });
  });

  const salariesText = useMemo(() => {
    let result = "";

    const currencyCode = request?.hiring_plan_data.currency?.code ?? null;
    const minSalary = request?.hiring_plan_data.salary_min;
    const maxSalary = request?.hiring_plan_data.salary_max;

    if (minSalary && maxSalary) {
      result = `${renderCurrency(minSalary, currencyCode)} — ${renderCurrency(maxSalary, currencyCode)}`;
    } else {
      if (minSalary) result = `${renderCurrency(minSalary, currencyCode)}`;
      if (maxSalary) result = `${renderCurrency(maxSalary, currencyCode)}`;
    }

    return result;
  }, [request]);

  const handleCancelRequest = () => {
    if (!request?.approval_request_details?.id) return;
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("approval:cancel_request_confirm_description"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        try {
          if (!request.approval_request_details) return;
          await requestCancel.mutateAsync(request?.approval_request_details.id);

          showInfo({ title: t("approval:request_canceled") });

          analyticsService.trackEvent(analyticEvents.hiringPlan.requestAction, {
            [analyticProperties.id]: request.approval_request_details?.id,
            [analyticProperties.title]: "Position Request",
            [analyticProperties.actionType]: "Cancel",
          });
        } catch {
          return;
        }
      },
    });
  };

  const handleDelete = () => {
    if (!request?.approval_request_details?.id) return;
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("core:delete_request_confirm_description"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        try {
          if (!request.approval_request_details) return;
          await requestDelete.mutateAsync(request?.approval_request_details.id);

          showInfo({ title: t("approval:request_deleted") });

          analyticsService.trackEvent(analyticEvents.approvals.requestAction, {
            [analyticProperties.id]: request.approval_request_details?.id,
            [analyticProperties.title]: "Position Request",
            [analyticProperties.actionType]: "Delete",
          });

          close(id);
        } catch {
          return;
        }
      },
    });
  };

  return (
    <Modal
      {...props}
      size={"lg"}
      title={t("hiring_plan:position_request")}
      withCloser={true}
      actions={
        <Stack gap={"sm"}>
          {request?.acl.can_reject || request?.acl.can_approve ? (
            <Stack gap={"smd"} className={"pt-1"}>
              <Stack gap={"sm"}>
                <Switch
                  size={"sm"}
                  checked={useComment}
                  onChange={setUseComment}
                  title={t("common:add_comment")}
                />
                {useComment && (
                  <Input
                    label={t("approval:comment_your_decision") + "..."}
                    type={"text"}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                )}
              </Stack>
              <Stack gap={"sm"} justify={"between"}>
                {request?.acl.can_approve && (
                  <Button
                    size={"lg"}
                    type={"submit"}
                    className={"w-full"}
                    isLoading={requestAnswer.isPending && isApproveButtonShouldLoading}
                    onClick={() => {
                      void handleAnswerRequest(true);
                    }}
                  >
                    {t("approval:approve")}
                  </Button>
                )}
                {request?.acl.can_reject && (
                  <Button
                    variant={"secondary"}
                    size={"lg"}
                    type={"submit"}
                    className={"w-full"}
                    isLoading={requestAnswer.isPending && !isApproveButtonShouldLoading}
                    onClick={() => {
                      void handleAnswerRequest(false);
                    }}
                  >
                    {t("approval:reject")}
                  </Button>
                )}
              </Stack>
            </Stack>
          ) : (
            <Button
              size={"lg"}
              className={"w-full"}
              variant={"secondary"}
              onClick={() => {
                close(id);
              }}
            >
              {t("common:close")}
            </Button>
          )}
          {request?.hiring_plan_data.status === ApiHiringPlanStatus.approved &&
            request?.hiring_plan_data.job && (
              <ButtonLink
                href={`/recruitment/candidates/?job_id=${request?.hiring_plan_data.job.id}`}
                size={"lg"}
                variant={"primary"}
                onClick={() => {
                  close(id);
                }}
              >
                {t("hiring_plan:view_job")}
              </ButtonLink>
            )}
        </Stack>
      }
      headerActionsSlot={(() => {
        const hasUpperMenuOptions =
          request?.acl.can_edit || request?.acl.can_cancel || request?.acl.can_bypass_flow;

        const canBypassFlow = canManageRequests && request?.acl.can_bypass_flow;

        const hasDeleteOption = request?.acl.can_delete;

        const shouldShowMenu = hasUpperMenuOptions || hasDeleteOption;

        const shouldShowDivider = hasUpperMenuOptions && hasDeleteOption;

        return shouldShowMenu ? (
          <DropdownMenu
            trigger={
              <Button
                variant={"secondary"}
                rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}
              >
                {t("common:actions")}
              </Button>
            }
          >
            {request.acl.can_edit && (
              <ListOption
                title={t("core:edit_request")}
                onClick={() => {
                  if (!request || !request.approval_request_details) return;
                  openModal(RequestPositionEditModal, {
                    request: request,
                    requestId: request.approval_request_details.id,
                  });
                }}
              />
            )}
            {request.acl.can_cancel && (
              <ListOption
                title={t("approval:cancel_request") + "..."}
                onClick={() => handleCancelRequest()}
              />
            )}
            {canBypassFlow && (
              <DropdownMenuItem
                title={t("approval:approve_as_admin") + "..."}
                description={t("approval:approve_as_admin_description")}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-crown text-warning" />}
                isSelected={false}
                onClick={() => handleAnswerRequest(true, true)}
              />
            )}
            {canBypassFlow && (
              <DropdownMenuItem
                title={t("approval:reject_as_admin") + "..."}
                description={t("approval:reject_as_admin_description")}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-crown text-warning" />}
                isSelected={false}
                onClick={() => handleAnswerRequest(false, true)}
              />
            )}
            {shouldShowDivider && <DropdownMenuSeparator />}
            {hasDeleteOption && (
              <ListOption
                titleSlot={
                  <Text className={"text-danger"}>{t("approval:delete_request") + "..."}</Text>
                }
                onClick={() => handleDelete()}
              />
            )}
          </DropdownMenu>
        ) : (
          <></>
        );
      })()}
    >
      <Stack>
        {isLoading && <Box className={"h-56"}></Box>}
        {request?.hiring_plan_data.date_plan && (
          <Text size={"lg"}>{renderDate(request?.hiring_plan_data.date_plan)}</Text>
        )}
        {request && (
          <>
            <Title
              size={"lg"}
              header={request.hiring_plan_data.title ?? ""}
              caption={captionTitle}
            ></Title>

            <Title
              header={t("common:details")}
              size={"xs"}
              paddingBottom={true}
              paddingTop={true}
            />

            <ListItem
              emphasis={"top"}
              title={t("common:status")}
              captionSlot={
                <Text
                  className={`font-semibold ${getHiringPlanRequestStatusColor(request.hiring_plan_data.status).textColor}`}
                >
                  {getHiringPlanRequestStatusTitle(request.hiring_plan_data.status)}
                </Text>
              }
              valueSlot={
                request?.acl.can_connect_job && (
                  <DropdownMenu
                    trigger={
                      <>
                        {!request?.hiring_plan_data.job ? (
                          <Button variant={"primary"} size={"sm"}>
                            {t("hiring_plan:link_job")}
                          </Button>
                        ) : (
                          <Button
                            variant={"secondary"}
                            size={"sm"}
                            rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}
                          >
                            {t("common:actions")}
                          </Button>
                        )}
                      </>
                    }
                  >
                    <>
                      <LinkTo
                        to={`/recruitment/jobs/new?position_request_id=${request?.approval_request_details?.id}`}
                        className={"decoration-0 no-underline"}
                        target={"_blank"}
                        onClick={() => {
                          sessionStorage.setItem(
                            "job_edit_referrer",
                            location.pathname + location.search
                          );
                        }}
                      >
                        <ListOption
                          title={
                            request?.hiring_plan_data.job
                              ? t("hiring_plan:create_another_job")
                              : t("hiring_plan:create_job")
                          }
                        />
                      </LinkTo>

                      <ListOption
                        onClick={handleLinkToJob}
                        title={
                          request?.hiring_plan_data.job
                            ? t("hiring_plan:link_another_exist_job")
                            : t("hiring_plan:link_exist_job")
                        }
                      />
                      {request?.hiring_plan_data.job && (
                        <ListOption onClick={handleUnlinkToJob} title={t("hiring_plan:unlink")} />
                      )}
                    </>
                  </DropdownMenu>
                )
              }
            />

            <ListItem
              emphasis={"middle"}
              preTitle={t("core:job_profile")}
              title={
                request.hiring_plan_data.job_profile
                  ? getJobProfileTitle(request.hiring_plan_data.job_profile)
                  : "-"
              }
            />
            <ListItem
              emphasis={"middle"}
              preTitle={t("common:location")}
              title={request.hiring_plan_data.location?.title ?? "-"}
            />

            <ListItem
              emphasis={"middle"}
              preTitle={t("core:department")}
              title={request.hiring_plan_data.department?.title ?? "-"}
            />

            <ListItem
              emphasis={"middle"}
              preTitle={t("core:division")}
              title={request.hiring_plan_data.division?.title ?? "-"}
            />

            <ListItem
              emphasis={"middle"}
              preTitle={t("hiring_plan:plan_date")}
              title={
                request.hiring_plan_data.date_plan
                  ? renderDate(request.hiring_plan_data.date_plan)
                  : "-"
              }
            />

            {(request?.hiring_plan_data.salary_min || request?.hiring_plan_data.salary_max) && (
              <ListItem emphasis={"middle"} preTitle={t("common:budget")} title={salariesText} />
            )}

            <ListItem
              emphasis={"middle"}
              preTitle={t("common:description")}
              titleSlot={
                <Text className={"whitespace-pre-line font-semibold"}>
                  {request.hiring_plan_data.description ?? "-"}
                </Text>
              }
            />

            <ListItem
              emphasis={"middle"}
              preTitle={t("hiring_plan:responsibilities")}
              titleSlot={<HtmlViewer content={request.hiring_plan_data.responsibilities ?? "-"} />}
            />

            <ListItem
              emphasis={"middle"}
              preTitle={t("hiring_plan:requirements")}
              titleSlot={<HtmlViewer content={request.hiring_plan_data.requirements ?? "-"} />}
            />

            {request?.hiring_plan_data.files && request.hiring_plan_data.files.length > 0 && (
              <Stack className={"pt-4"} gap={"sm"}>
                {request.hiring_plan_data.files.map((file) => (
                  <FileItem key={`file${file.id}`} file={file} />
                ))}
              </Stack>
            )}

            {request.approval_request_details && (
              <ApprovalsPersons request={request.approval_request_details} />
            )}

            {request?.hiring_plan_data.job && (
              <>
                <Title
                  header={t("hiring_plan:linked_job")}
                  paddingTop={true}
                  paddingBottom={true}
                  size={"xs"}
                />
                <LinkTo
                  to={`/recruitment/candidates/?job_id=${request?.hiring_plan_data.job.id}`}
                  className={"decoration-0 no-underline"}
                  target={"_blank"}
                  onClick={() => {
                    close(id);
                  }}
                >
                  <Stack direction={"horizontal"} className={"items-center"} gap={"md"}>
                    <Avatar size={"md"} emoji={request?.hiring_plan_data.job.emoji} />
                    <Stack>
                      <Text className={"text-title"}>{request?.hiring_plan_data.job.title}</Text>
                      <Text className={"text-secondary"}>
                        {
                          JobStatusItems().find(
                            (x) => x.id == request?.hiring_plan_data.job?.status
                          )?.title
                        }
                      </Text>
                    </Stack>
                  </Stack>
                </LinkTo>
              </>
            )}

            {request.hiring_plan_data.hired_applicants.length > 0 && (
              <Stack>
                <Title
                  header={t("ats:hired_candidates")}
                  paddingTop={true}
                  paddingBottom={true}
                  size={"xs"}
                />
                {request?.hiring_plan_data.hired_applicants.map((applicant) => (
                  <LinkTo
                    to={`/recruitment/candidates/${applicant.id}`}
                    target={"_blank"}
                    className={"decoration-0 no-underline"}
                    onClick={() => {
                      close(id);
                    }}
                  >
                    <Stack direction={"horizontal"} className={"items-center"} gap={"md"}>
                      <Avatar size={"md"} url={applicant.photo_url} />
                      <Stack>
                        <Text
                          className={"text-title"}
                        >{`${applicant.first_name} ${applicant.last_name}`}</Text>
                        <Text className={"text-secondary"}>
                          {request?.hiring_plan_data.job?.title}
                        </Text>
                      </Stack>
                    </Stack>
                  </LinkTo>
                ))}
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Modal>
  );
}
